import { useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDebounce } from "@/shared/hooks/useDebounce";
import type {
  DispatchReadinessItem,
  DispatchReadinessOrder,
} from "../../services/ordersApi";
import { useApproveDispatch, useDispatchReadinessQueue } from "../../hooks/useOrders";

const formatQty = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);

interface SelectionState {
  [orderItemId: string]: number;
}

export const DispatchReadinessPanel = ({ active }: { active: boolean }) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const queueQuery = useDispatchReadinessQueue(
    { search: debouncedSearch || undefined, pageNumber, pageSize },
    active
  );
  const approveMutation = useApproveDispatch();

  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(new Set());
  const [selection, setSelection] = useState<SelectionState>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [method, setMethod] = useState<1 | 2>(1);
  const [deliveryLocations, setDeliveryLocations] = useState<Record<string, string>>({});

  // Reset page and transient selection when the view changes — adjusted during
  // render (React's "adjust state when props change" pattern) instead of effects.
  const [lastSearch, setLastSearch] = useState(debouncedSearch);
  if (lastSearch !== debouncedSearch) {
    setLastSearch(debouncedSearch);
    setPageNumber(1);
  }

  const resetKey = `${debouncedSearch}|${pageNumber}|${pageSize}`;
  const [lastResetKey, setLastResetKey] = useState(resetKey);
  if (resetKey !== lastResetKey) {
    setLastResetKey(resetKey);
    setSelection({});
    setExpandedOrderIds(new Set());
  }

  const orders = useMemo(() => queueQuery.data?.items ?? [], [queueQuery.data]);
  const selectedCount = Object.keys(selection).length;
  const selectedOrders = orders.filter((order) => order.items.some((item) => item.orderItemId in selection));
  const changedOrders = selectedOrders.filter((order) => {
    const savedMethod = fulfilmentMethodNumber(order.fulfilmentMethod);
    return savedMethod !== undefined && savedMethod !== method;
  });
  const collectionToDeliveryOrders = method === 1
    ? changedOrders.filter((order) => fulfilmentMethodNumber(order.fulfilmentMethod) === 2)
    : [];
  const hasMissingDeliveryLocation = collectionToDeliveryOrders.some(
    (order) => !deliveryLocations[order.orderId]?.trim()
  );

  const openConfirm = () => {
    const savedMethods = selectedOrders
      .map((order) => fulfilmentMethodNumber(order.fulfilmentMethod))
      .filter((value): value is 1 | 2 => value !== undefined);
    const firstSavedMethod = savedMethods[0];
    setMethod(firstSavedMethod ?? 1);
    setDeliveryLocations(Object.fromEntries(
      selectedOrders.map((order) => [order.orderId, order.deliveryLocation ?? ""])
    ));
    setConfirmOpen(true);
  };

  const toggleExpanded = (orderId: string) => {
    setExpandedOrderIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleItem = (item: DispatchReadinessItem) => {
    setSelection((current) => {
      if (Object.prototype.hasOwnProperty.call(current, item.orderItemId)) {
        const next = { ...current };
        delete next[item.orderItemId];
        return next;
      }
      if (item.readyToRelease <= 0) return current;
      return { ...current, [item.orderItemId]: item.readyToRelease };
    });
  };

  const closeDialog = () => {
    setConfirmOpen(false);
  };

  const submitApproval = async () => {
    if (selectedCount === 0) return;
    try {
      const payload = {
        method,
        orders: selectedOrders.map((order) => ({
          orderId: order.orderId,
          deliveryLocation: deliveryLocations[order.orderId] ?? order.deliveryLocation ?? "",
        })),
        items: Object.entries(selection).map(([orderItemId, quantity]) => ({
          orderItemId,
          quantity,
        })),
      };
      const results = await approveMutation.mutateAsync(payload);
      toast.success(`Released ${results.length} item(s) for ${method === 1 ? "Delivery" : "Customer Collection"}.`);
      setConfirmOpen(false);
    } catch (error) {
      setSelection({});
      await queueQuery.refetch();
      toast.error(error instanceof Error ? error.message : "Could not approve items.");
    }
  };

  if (!active) return null;

  if (queueQuery.isLoading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Loading dispatch readiness queue...</div>
    );
  }

  if (queueQuery.error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load the dispatch readiness queue.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Input
          placeholder="Search by order number or customer"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="max-w-md"
        />
        <div className="flex items-center gap-2">
          {orders[0]?.stockUpdatedAt ? (
            <span className="text-xs text-muted-foreground">
              Live stock updated {new Date(orders[0].stockUpdatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          ) : null}
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => queueQuery.refetch()}
            disabled={queueQuery.isFetching}
            title="Refresh live stock"
            aria-label="Refresh live stock"
          >
            <RefreshCw className={`h-4 w-4 ${queueQuery.isFetching ? "animate-spin" : ""}`} />
          </Button>
            <Button disabled={selectedCount === 0} onClick={openConfirm}>
            Release for Fulfilment{selectedCount > 0 ? ` (${selectedCount})` : ""}
          </Button>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="rounded-lg border bg-muted/40 p-8 text-center text-muted-foreground">
          No orders with production-ready stock awaiting approval.
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead>Order Number</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Order Date</TableHead>
                <TableHead>Items Ready</TableHead>
                <TableHead>Total Ready Qty</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <OrderRow
                  key={order.orderId}
                  order={order}
                  expanded={expandedOrderIds.has(order.orderId)}
                  onToggle={() => toggleExpanded(order.orderId)}
                  selection={selection}
                  onToggleItem={toggleItem}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <PaginationBar
        currentPage={pageNumber}
        pageSize={pageSize}
        totalCount={queueQuery.data?.totalCount ?? 0}
        totalPages={queueQuery.data?.totalPages ?? 1}
        onPageChange={setPageNumber}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageNumber(1);
        }}
      />

      <Dialog open={confirmOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Release for Fulfilment</DialogTitle>
            <DialogDescription>
              Choose the customer fulfilment method and confirm each quantity.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-1 text-sm">
            <span className="font-medium">Fulfilment method</span>
            <Select value={String(method)} onValueChange={(value) => setMethod(Number(value) as 1 | 2)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Delivery</SelectItem>
                <SelectItem value="2">Customer Collection</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {changedOrders.length > 0 && (
            <div role="status" className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">This changes the fulfilment method saved on {changedOrders.length} order(s).</p>
                {collectionToDeliveryOrders.length > 0 && (
                  <p className="mt-1">Enter a delivery location for each order changing from customer collection.</p>
                )}
              </div>
            </div>
          )}

          {collectionToDeliveryOrders.length > 0 && (
            <div className="space-y-3">
              {collectionToDeliveryOrders.map((order) => (
                <div key={order.orderId} className="grid gap-1.5 text-sm">
                  <span className="font-medium">Delivery location · {order.orderNumber}</span>
                  <Input
                    value={deliveryLocations[order.orderId] ?? ""}
                    onChange={(event) => setDeliveryLocations((current) => ({
                      ...current,
                      [order.orderId]: event.target.value,
                    }))}
                    placeholder="Enter delivery location"
                    aria-label={`Delivery location for ${order.orderNumber}`}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            {Object.entries(selection).map(([orderItemId, qty]) => {
              const item = findSelectedItem(orders, orderItemId);
              if (!item) return null;
              return (
                <div key={orderItemId} className="flex items-center gap-2 rounded-md border p-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium leading-tight break-words">{itemLabel(item)}</p>
                    <p className="text-xs text-muted-foreground">
                      Live stock {formatQty(item.currentAvailableStock)} · Ready to release{" "}
                      {formatQty(item.readyToRelease)}
                    </p>
                  </div>
                  <Input
                    type="number"
                    min={1}
                    max={item.readyToRelease}
                    step="any"
                    value={String(qty)}
                    onChange={(event) => {
                      const parsed = Number(event.target.value);
                      setSelection((current) => ({
                        ...current,
                        [orderItemId]: Number.isFinite(parsed)
                          ? Math.min(Math.max(parsed, 1), item.readyToRelease)
                          : current[orderItemId],
                      }));
                    }}
                    className="w-16 shrink-0"
                  />
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={closeDialog}>Cancel</Button>
            <Button onClick={submitApproval} disabled={approveMutation.isPending || hasMissingDeliveryLocation}>
              {approveMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Releasing...
                </>
              ) : (
                `Release ${selectedCount} item(s)`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const fulfilmentMethodNumber = (method: DispatchReadinessOrder["fulfilmentMethod"]): 1 | 2 | undefined => {
  if (method === 1 || method === "Delivery") return 1;
  if (method === 2 || method === "CustomerCollection") return 2;
  return undefined;
};

const findSelectedItem = (
  orders: DispatchReadinessOrder[],
  orderItemId: string
): DispatchReadinessItem | undefined =>
  orders.flatMap((o) => o.items).find((i) => i.orderItemId === orderItemId);

const itemLabel = (item: DispatchReadinessItem) => {
  const name = item.variety ? `${item.productName} - ${item.variety}` : item.productName;
  return item.trayTypeLabel ? `${name} (${item.trayTypeLabel})` : name;
};

interface OrderRowProps {
  order: DispatchReadinessOrder;
  expanded: boolean;
  onToggle: () => void;
  selection: SelectionState;
  onToggleItem: (item: DispatchReadinessItem) => void;
}

const OrderRow = ({
  order,
  expanded,
  onToggle,
  selection,
  onToggleItem,
}: OrderRowProps) => {
  const releasableItems = order.items.filter((item) => item.readyToRelease > 0);
  const allSelected = releasableItems.length > 0 &&
    releasableItems.every((item) => item.orderItemId in selection);

  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </TableCell>
        <TableCell className="font-mono text-xs">{order.orderNumber}</TableCell>
        <TableCell>{order.customerName}</TableCell>
        <TableCell>
          {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : "-"}
        </TableCell>
        <TableCell>{order.itemsReadyCount}</TableCell>
        <TableCell>{formatQty(order.totalReadyQuantity)}</TableCell>
        <TableCell>
          <Badge variant="outline">{order.status}</Badge>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={7} className="bg-muted/30 p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 pl-6">
                    <Checkbox
                      checked={
                        allSelected
                      }
                      disabled={releasableItems.length === 0}
                      onCheckedChange={() => {
                        releasableItems.forEach((item) => {
                          const isSelected = item.orderItemId in selection;
                          if (allSelected && isSelected) onToggleItem(item);
                        });
                        if (!allSelected) {
                          releasableItems.forEach((item) => {
                            if (!(item.orderItemId in selection)) onToggleItem(item);
                          });
                        }
                      }}
                      aria-label="Select all ready items"
                    />
                  </TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Variety</TableHead>
                  <TableHead>Ordered Qty</TableHead>
                  <TableHead>Production Ready</TableHead>
                  <TableHead>Live Available Stock</TableHead>
                  <TableHead>Already Released</TableHead>
                  <TableHead>Ready to Release</TableHead>
                  <TableHead>Readiness</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {order.items.map((item) => (
                  <TableRow key={item.orderItemId}>
                    <TableCell className="pl-6">
                      <Checkbox
                        checked={item.orderItemId in selection}
                        disabled={item.readyToRelease <= 0}
                        onCheckedChange={() => onToggleItem(item)}
                        aria-label={`Select ${item.productName}`}
                      />
                    </TableCell>
                    <TableCell>
                      {item.productName}
                      {item.trayTypeLabel ? (
                        <span className="text-xs text-muted-foreground"> ({item.trayTypeLabel})</span>
                      ) : null}
                    </TableCell>
                    <TableCell>{item.variety ?? "-"}</TableCell>
                    <TableCell>
                      {formatQty(item.orderedQuantity)} {item.unitLabel}
                    </TableCell>
                    <TableCell>{formatQty(item.quantityProductionReady)}</TableCell>
                    <TableCell className="font-medium">{formatQty(item.currentAvailableStock)}</TableCell>
                    <TableCell>{formatQty(item.quantityApprovedForDispatch)}</TableCell>
                    <TableCell className="font-medium">
                      {formatQty(item.readyToRelease)}
                    </TableCell>
                    <TableCell>
                      <ReadinessBadge status={item.readinessStatus} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const ReadinessBadge = ({ status }: { status: DispatchReadinessItem["readinessStatus"] }) => {
  const label = status === "Ready" ? "Ready" : status === "PartiallyReady" ? "Partially Ready" : "Not Ready";
  const dotClass = status === "Ready" ? "bg-emerald-500" : status === "PartiallyReady" ? "bg-amber-500" : "bg-slate-400";
  return (
    <Badge variant="outline" className="gap-1.5 whitespace-nowrap">
      <span className={`h-2 w-2 rounded-full ${dotClass}`} aria-hidden="true" />
      {label}
    </Badge>
  );
};

export default DispatchReadinessPanel;
